import type { KgInsight, KgInsightCategory } from '@api';
import { Tab, TabLines } from '@design/components/tab-lines/TabLines';
import { KgInsightArticle } from '@terminal/views/kg-insight/components/kg-insight-view/KgInsightArticle';
import { KgInsightView } from '@terminal/views/kg-insight/components/kg-insight-view/KgInsightView';
import { KgInsightSkeleton } from '@terminal/widgets/catalyst-insights-drawer/catalyst-widget/components/kg-insight-content/KgInsightSkeleton';
import {
  EarningsInsightEmptyState,
  EarningsInsightEmptyStateVariant,
} from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-empty-state/EarningsInsightEmptyState';
import { EarningsPreview } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/earnings-preview/EarningsPreview';
import { EarningsRecap } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/earnings-recap/EarningsRecap';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useEarningsPreviewAndRecapInsights } from '../../hooks/useEarningsPreviewAndRecapInsights';
import styles from './PreviewAndRecap.module.scss';

type PreviewAndRecapProps = {
  earningsId: string;
  entityTag: string;
};

interface RenderInsightPanelArgs {
  kgInsight: KgInsight | undefined;
  isLoading: boolean;
  emptyStateVariant: EarningsInsightEmptyStateVariant;
}

const renderInsightPanel = ({
  kgInsight,
  isLoading,
  emptyStateVariant,
}: RenderInsightPanelArgs) => {
  if (isLoading) {
    return (
      <main className={styles.loadingWrapper}>
        <KgInsightSkeleton />
      </main>
    );
  }

  if (!kgInsight) {
    return (
      <EarningsInsightEmptyState variant={emptyStateVariant} unsetMinHeight />
    );
  }

  return (
    <KgInsightView kgInsight={kgInsight} subject={kgInsight.subject}>
      {({ setSectionRef }) => (
        <KgInsightArticle kgInsight={kgInsight} setSectionRef={setSectionRef} />
      )}
    </KgInsightView>
  );
};

interface ShouldShowKgPanelArgs {
  insightId: string;
  isLoading: boolean;
  insight: KgInsight | undefined;
}

const shouldShowKgPanel = ({
  insightId,
  isLoading,
  insight,
}: ShouldShowKgPanelArgs) => !!insightId && (isLoading || !!insight);

export const PreviewAndRecap = ({
  earningsId,
  entityTag,
}: PreviewAndRecapProps) => {
  const { t } = useTranslation(['earnings']);
  const [activeTab, setActiveTab] =
    useState<KgInsightCategory>('earnings_recap');

  const {
    previewInsight,
    previewInsightId,
    recapInsight,
    recapInsightId,
    isPreviewLoading,
    isRecapLoading,
    hasRecapData,
    isRecapAvailabilityLoading,
  } = useEarningsPreviewAndRecapInsights(earningsId);

  useEffect(() => {
    if (!isRecapAvailabilityLoading && !hasRecapData) {
      setActiveTab('earnings_preview');
    }
  }, [isRecapAvailabilityLoading, hasRecapData]);

  const tabs: Tab<KgInsightCategory>[] = [
    {
      tabKey: 'earnings_preview',
      tabLabel: t('earnings:earningsInsight.drawer.earningsPreview.title'),
    },
    {
      tabKey: 'earnings_recap',
      tabLabel: t('earnings:earningsInsight.drawer.earningsRecap.title'),
    },
  ];

  const showPreviewKgPanel = shouldShowKgPanel({
    insightId: previewInsightId,
    isLoading: isPreviewLoading,
    insight: previewInsight,
  });
  const showRecapKgPanel = shouldShowKgPanel({
    insightId: recapInsightId,
    isLoading: isRecapLoading,
    insight: recapInsight,
  });

  return (
    <div className={styles.root}>
      <div className={styles.tabs}>
        <TabLines
          tabs={tabs}
          activeTab={activeTab}
          onChange={(_index, tabKey) => setActiveTab(tabKey)}
        />
      </div>
      <div className={styles.panel}>
        <div
          className={clsx(styles.panelContent, {
            [styles.scrollable]: !showPreviewKgPanel,
          })}
          hidden={activeTab !== 'earnings_preview'}
        >
          {showPreviewKgPanel ? (
            renderInsightPanel({
              kgInsight: previewInsight,
              isLoading: isPreviewLoading,
              emptyStateVariant: 'preview-unavailable',
            })
          ) : (
            <EarningsPreview
              expandCitations={false}
              earningsId={earningsId}
              entityTag={entityTag}
              unsetMinHeight
            />
          )}
        </div>
        <div
          className={clsx(styles.panelContent, {
            [styles.scrollable]: !showRecapKgPanel,
          })}
          hidden={activeTab !== 'earnings_recap'}
        >
          {showRecapKgPanel ? (
            renderInsightPanel({
              kgInsight: recapInsight,
              isLoading: isRecapLoading,
              emptyStateVariant: 'recap-unavailable',
            })
          ) : (
            <EarningsRecap
              expandCitations={false}
              earningsId={earningsId}
              entityTag={entityTag}
              unsetMinHeight
            />
          )}
        </div>
      </div>
    </div>
  );
};
